import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Check, Download, Trash2, Mail, RefreshCw, Undo, X } from 'lucide-react';
import { CookieConsent, defaultConsent, euDefaultConsent } from '@/types';
import { useCookieConsent } from '@/src/hooks/useCookieConsent';
import { isEURegion } from '@/src/utils/region';
import { ToggleSwitch } from '@/src/components/ui/ToggleSwitch';
import { useAuth } from '@/src/contexts/AuthContext';

interface PrivacySettingsProps {
  isModal?: boolean;
  onClose?: () => void;
  user?: any;
  userProfile?: any;
  savedVentures?: any[];
  openedFromSidebar?: boolean;
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({
  isModal = false,
  onClose,
  user,
  userProfile,
  savedVentures = [],
  openedFromSidebar = false,
}) => {
  const { isLoggedIn, setIsSignInModalOpen } = useAuth();
  const { consent, updateConsent } = useCookieConsent();
  const [localConsent, setLocalConsent] = useState<CookieConsent | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (consent) {
      setLocalConsent(consent);
    }
  }, [consent]);

  const handleToggle = (key: keyof CookieConsent, value: boolean) => {
    if (key === 'essential') return; // Cannot toggle essential
    setLocalConsent((prev) => (prev ? { ...prev, [key]: value } : null));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (localConsent) {
      updateConsent(localConsent);
      setHasChanges(false);
      setSuccessMessage('Preferences saved successfully');
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    }
  };

  const handleReset = () => {
    const isEU = isEURegion();
    const resetConsent = isEU ? euDefaultConsent : defaultConsent;
    setLocalConsent({ ...resetConsent, timestamp: Date.now() });
    setHasChanges(true);
  };

  const handleWithdrawAll = () => {
    if (localConsent) {
      setLocalConsent({
        ...localConsent,
        functional: false,
        analytics: false,
        aiPerformance: false,
        timestamp: Date.now(),
      });
      setHasChanges(true);
    }
  };

  const exportConsentRecord = () => {
    const record = {
      platform: 'eNvesta AI',
      version: '1.0',
      consent: localConsent,
      exportedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eNvesta AI-consent-record-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRequestDataExport = () => {
    // Generate ISO/GDPR structured report
    const reportData = {
      exportMetadata: {
        timestamp: Date.now(),
        exportedBy: user?.email || 'Anonymous',
        systemIdentifier: 'eNvesta AI GDPR Export Port',
        tier: userProfile?.subscriptionTier || 'Free',
      },
      consentRecord: localConsent,
      userProfile: userProfile || { role: 'Anonymous User Preview' },
      archivedVentures: savedVentures,
    };
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const u = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = u;
    link.download = `envesta_gdpr_data_export_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(u);
  };

  const handleRequestDataDeletion = () => {
    setShowDeleteConfirm(true);
  };

  const confirmAccountDeletion = () => {
    if (typeof window !== 'undefined') {
      alert('Delete request registered. A secure purging protocol has been queued. Our DPO will contact you at ' + (user?.email || 'your registered email') + ' to finalize identity verification within 72 hours.');
      setShowDeleteConfirm(false);
    }
  };

  const formatConsentDate = (timestamp?: number) => {
    if (!timestamp) return 'No record found';
    try {
      const date = new Date(timestamp);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      };
      return date.toLocaleString('en-US', options) + ' EAT';
    } catch (e) {
      return 'June 15, 2026 at 14:30 EAT';
    }
  };

  const regionDetected = isEURegion() ? 'EU / EEA / UK region detected' : 'Uganda, East Africa & Rest of World (Implied Consent)';

  const content = (
    <div className="text-white w-full">
      {/* Header section (only if not a modal, or custom title block) */}
      {!isModal && (
        <header className="border-b border-white/[0.06] pb-6 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F26522] block mb-1 font-heading">
              eNvesta AI Compliance
            </span>
            <h1 className="text-2xl font-bold tracking-tight text-white leading-none font-heading">
              Privacy Settings
            </h1>
          </div>
          <div className="text-left md:text-right">
            <span className="text-[10px] font-medium uppercase tracking-widest text-white/60 block font-heading">
              GDPR & Uganda DPPA Suite
            </span>
            <span className="text-[10px] text-white/40 block mt-0.5">
              Active Session Security Protocol
            </span>
          </div>
        </header>
      )}

      {/* Main Grid: Cookies, Record & Rights */}
      <div className="space-y-8">
        
        {/* Section 1: Cookie Preferences */}
        <section className="bg-[#3a3430] border border-white/[0.06] p-6 md:p-8 rounded-xl">
          <div className="border-b border-white/[0.06] pb-4 mb-6">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.2em] text-white font-heading">
              Cookie Preferences
            </h3>
            <p className="text-[11px] text-white/60 font-normal mt-1 leading-relaxed">
              Define the level of persistent local storage active on your device.
            </p>
          </div>

          {localConsent && (
            <div className="space-y-1">
              <ToggleSwitch
                label="Essential"
                description="Required for secure logins, active billing sessions (Dodo Payments), and basic secure anti-CSRF state. Cannot be disabled."
                enabled={true}
                locked={true}
                onChange={() => {}}
              />
              <ToggleSwitch
                label="Functional"
                description="Remember your dashboard canvas layouts, selected currency filters, regional variables, and customized startup discovery feeds."
                enabled={localConsent.functional}
                onChange={(val) => handleToggle('functional', val)}
              />
              <ToggleSwitch
                label="Analytics"
                description="Anonymized metric indicators to gauge app loading latency and feature engagement, optimizing services for East African ecosystem networks."
                enabled={localConsent.analytics}
                onChange={(val) => handleToggle('analytics', val)}
              />
              <ToggleSwitch
                label="AI Performance"
                description="Maintains active model context queues during live sector intelligence scans, accelerating Gemini AI generation times."
                enabled={localConsent.aiPerformance}
                onChange={(val) => handleToggle('aiPerformance', val)}
              />
            </div>
          )}

          {/* Action buttons for preferences */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-8 pt-6 border-t border-white/[0.06]">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="text-[10px] font-bold uppercase tracking-widest border border-white/10 text-white/80 hover:bg-white/5 hover:text-white rounded-lg py-2.5 px-4 transition-all cursor-pointer font-heading"
                id="privacy-reset-default"
              >
                Reset to Default
              </button>
              <button
                type="button"
                onClick={handleWithdrawAll}
                className="text-[10px] font-bold uppercase tracking-widest border border-[#F26522]/30 text-[#F26522] hover:bg-[#F26522]/10 rounded-lg py-2.5 px-4 transition-all cursor-pointer font-heading"
                id="privacy-withdraw-all"
              >
                Withdraw All Consent
              </button>
            </div>

            <div className="flex items-center gap-3 self-end sm:self-auto">
              {hasChanges && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#F26522] animate-pulse font-heading">
                  Unsaved changes
                </span>
              )}
              {successMessage && (
                <span className="text-[10px] font-medium text-emerald-400">
                  ✓ {successMessage}
                </span>
              )}
              <button
                type="button"
                onClick={handleSave}
                className={`text-[10px] font-bold uppercase tracking-widest rounded-lg py-2.5 px-6 transition-all cursor-pointer font-heading ${
                  hasChanges
                    ? 'bg-[#F26522] text-white hover:bg-[#F26522]/90'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                id="privacy-save-changes"
              >
                Save Changes
              </button>
            </div>
          </div>
        </section>

        {/* Section 2: Your Consent Record */}
        {(!isLoggedIn || !openedFromSidebar) ? (
          <section className="bg-[#3a3430] border border-white/[0.06] p-6 md:p-8 rounded-xl">
            <div className="border-b border-white/[0.06] pb-4 mb-6">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading">
                Your Consent Record
              </h3>
              <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
                Read-only cryptographically linked regional and compliance audit parameter records.
              </p>
            </div>
            <div className="bg-[#322D29] p-6 border border-white/[0.06] rounded-lg flex flex-col items-center justify-center text-center space-y-3">
              <Lock className="w-5 h-5 text-white/40" />
              <div className="max-w-md">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white mb-1 font-heading">
                  {!isLoggedIn ? 'AUTHENTICATION REQUIRED' : 'ACCESS RESTRICTED'}
                </p>
                <p className="text-[10px] text-white/60 leading-relaxed">
                  {!isLoggedIn 
                    ? 'Consent history extraction requires an authenticated account. Sign in to view.' 
                    : 'Detailed audit parameters are restricted. Accessible only via the Profile Sidebar.'}
                </p>
              </div>
              {!isLoggedIn && (
                <button
                  type="button"
                  onClick={() => {
                    setIsSignInModalOpen(true);
                    if (isModal && onClose) onClose();
                  }}
                  className="text-[10px] font-bold uppercase tracking-widest bg-[#F26522] text-white hover:bg-[#F26522]/90 py-2 px-4 transition-all cursor-pointer rounded-lg font-heading"
                >
                  Verify Identity
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="bg-[#3a3430] border border-white/[0.06] p-6 md:p-8 rounded-xl">
            <div className="border-b border-white/[0.06] pb-4 mb-6">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.2em] text-white font-heading">
                Your Consent Record
              </h3>
              <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
                Read-only cryptographically linked regional and compliance audit parameter records.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px] mb-8">
              <div className="bg-[#322D29] p-4 border border-white/[0.06] rounded-lg flex flex-col justify-between">
                <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 block mb-2 font-heading">Region Detected</span>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold text-white">
                    {localConsent?.region === 'EU' ? '📌 United Kingdom / European Union (EU/EEA)' : '🌍 Uganda / East Africa (Implied Consent)'}
                  </span>
                </div>
                <p className="text-[9px] text-white/40 mt-2 leading-normal">
                  Determined securely on device via environment timezone resolve variables. No external lookups are performed.
                </p>
              </div>

              <div className="bg-[#322D29] p-4 border border-white/[0.06] rounded-lg space-y-3">
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 block font-heading">Consent Timestamp</span>
                  <span className="font-semibold text-white">{formatConsentDate(localConsent?.timestamp)}</span>
                </div>
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 block font-heading">Active Status indicators</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">Essential</span>
                    {localConsent?.functional && <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">Functional</span>}
                    {localConsent?.analytics && <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">Analytics</span>}
                    {localConsent?.aiPerformance && <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">AI Engine</span>}
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={exportConsentRecord}
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-white/10 hover:bg-white/5 text-white rounded-lg py-2.5 px-6 transition-all cursor-pointer font-heading"
              id="privacy-export-record"
            >
              <Download className="w-3.5 h-3.5" /> Export Consent Record
            </button>
          </section>
        )}

        {/* Section 3: Data Rights (GDPR / Uganda DPPA) */}
        {(!isLoggedIn || !openedFromSidebar) ? (
          <section className="bg-[#3a3430] border border-white/[0.06] p-6 md:p-8 rounded-xl">
            <div className="border-b border-white/[0.06] pb-4 mb-6">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/40 font-heading">
                Data Rights (GDPR / Uganda DPPA)
              </h3>
              <p className="text-[11px] text-white/40 mt-1 leading-relaxed">
                Exercise your compliance boundaries under European GDPR and Ugandan Data Protection regulations.
              </p>
            </div>
            <div className="bg-[#322D29] p-6 border border-white/[0.06] rounded-lg flex flex-col items-center justify-center text-center space-y-3">
              <Lock className="w-5 h-5 text-white/40" />
              <div className="max-w-md">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white mb-1 font-heading">
                  {!isLoggedIn ? 'IDENTITY VERIFICATION REQUIRED' : 'ACCESS RESTRICTED'}
                </p>
                <p className="text-[10px] text-white/60 leading-relaxed">
                  {!isLoggedIn 
                    ? 'GDPR/Uganda DPPA data queries and deletion operations require a verified account. Sign in to access.' 
                    : 'System data extraction components are restricted. Open Privacy Settings directly from the Profile Sidebar to access.'}
                </p>
              </div>
              {!isLoggedIn && (
                <button
                  type="button"
                  onClick={() => {
                    setIsSignInModalOpen(true);
                    if (isModal && onClose) onClose();
                  }}
                  className="text-[10px] font-bold uppercase tracking-widest bg-[#F26522] text-white hover:bg-[#F26522]/90 py-2 px-4 transition-all cursor-pointer rounded-lg font-heading"
                >
                  Sign In
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="bg-[#3a3430] border border-white/[0.06] p-6 md:p-8 rounded-xl">
            <div className="border-b border-white/[0.06] pb-4 mb-6">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.2em] text-white font-heading">
                Data Rights (GDPR / Uganda DPPA)
              </h3>
              <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
                Exercise your compliance boundaries under European GDPR and Ugandan Data Protection regulations.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4">
              <button
                type="button"
                onClick={handleRequestDataExport}
                className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-white/10 hover:bg-white/5 text-white rounded-lg py-3 px-6 transition-all cursor-pointer font-heading"
                id="privacy-data-export"
              >
                <Download className="w-3.5 h-3.5" /> Request Data Export
              </button>

              <button
                type="button"
                onClick={handleRequestDataDeletion}
                className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-lg py-3 px-6 transition-all cursor-pointer font-heading"
                id="privacy-data-deletion"
              >
                <Trash2 className="w-3.5 h-3.5" /> Request Data Deletion
              </button>

              <a
                href="mailto:dpo@eNvestaai.com?subject=eNvesta GDPR / DPPA Right Request"
                className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-white/10 text-white/80 hover:bg-white/5 hover:text-white rounded-lg py-3 px-6 transition-all text-center font-heading"
                id="privacy-contact-dpo"
              >
                <Mail className="w-3.5 h-3.5" /> Contact DPO
              </a>
            </div>
          </section>
        )}

      </div>

      {/* Account Deletion Modal dialog inside PrivacySettings */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
          <div className="bg-[#3a3430] border border-red-500/30 max-w-sm w-full p-8 text-left rounded-xl" id="delete-alert-box">
            <h4 className="text-sm font-bold uppercase tracking-widest text-[#F26522] mb-2 font-heading">
              ⚠️ Delete Account?
            </h4>
            <p className="text-[11px] text-white/60 leading-relaxed mb-6">
              This will permanently delete your account, wiping saved venture dossiers, SWOT evaluations, search history, and all linked data.
            </p>
            <div className="flex items-center justify-end gap-3 font-heading text-[10px] font-bold tracking-widest">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-white/60 hover:text-white px-3 py-2 cursor-pointer font-heading"
              >
                Cancel
              </button>
              <button
                onClick={confirmAccountDeletion}
                className="bg-red-600 text-white hover:bg-red-700 px-5 py-2.5 rounded-lg cursor-pointer font-heading"
              >
                Yes, Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Return the visual box inside either Modal container or normal page grid
  if (isModal) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
        id="privacy-settings-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl bg-[#322D29] border border-white/[0.06] rounded-xl p-8 md:p-10 text-left max-h-[90vh] overflow-y-auto"
          id="privacy-settings-modal"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors cursor-pointer z-10 p-1 hover:bg-white/5 rounded-lg"
            aria-label="Close settings"
          >
            <X className="w-5 h-5" />
          </button>

          <header className="border-b border-white/[0.06] pb-4 mb-6 shrink-0 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F26522] block mb-1 font-heading">
                eNvesta AI Compliance
              </span>
              <h2 className="text-xl font-bold uppercase tracking-tight text-white font-heading">
                Privacy Settings
              </h2>
            </div>
            <div className="text-left md:text-right">
              <span className="text-[10px] font-medium uppercase tracking-widest text-white/60 block font-heading">
                GDPR & Uganda DPPA Suite
              </span>
            </div>
          </header>

          {content}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#322D29] p-6 md:p-10 border border-white/[0.06] rounded-xl select-none" id="privacy-settings-page">
      {content}
    </div>
  );
};
